import React, { useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import AIActionButton from 'tripPlanner/components/Buttons/AIActionButton'
import type { TripStop } from 'tripPlanner/api/types'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'

const Box = styled.div<{ $compact: boolean }>`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${rem(16)};
  padding: ${rem(16)};
  border: 1px dashed ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};

  ${props => props.$compact && css`
    gap: ${rem(16)};
    padding: ${rem(12)} ${rem(16)};
    border: none;
    border-radius: 0;
    background: ${props.theme.palette.neutral.surface.normalBackground};
  `}
`
const Copy = styled.div<{ $compact: boolean }>`
  flex: ${props => props.$compact ? '0 1 auto' : `1 1 ${rem(220)}`};
  min-width: 0;
`

interface Props {
  stop: TripStop
  onSendMessage: (message: string) => Promise<void> | void
  disabled?: boolean
  variant?: 'default' | 'list'
}

export default function StopDestinationRefinement({ stop, onSendMessage, disabled, variant = 'default' }: Props) {
  const compact = variant === 'list'
  const intl = useIntl()
  const eventBus = useTripPlannerEventBus()
  const [sending, setSending] = useState(false)
  const [sendFailed, setSendFailed] = useState(false)
  const prompt = stop.destinationRefinementPrompt?.trim()
  const placeName = stop.placeName.trim()
  const handleChoose = useCallback(async() => {
    setSending(true)
    setSendFailed(false)
    try {
      eventBus.dispatch({ type: 'focus_chat' })
      await onSendMessage(appendContextTag(
        placeName
          ? intl.formatMessage({
              id: 'pCgQZ7H8pKS7wmW_Tds4',
              defaultMessage: '{placeName} — help me choose where to go.',
              description: 'Chat message sent when a traveller asks to refine an undecided itinerary destination',
            }, { placeName })
          : intl.formatMessage({
              id: '9dKVLv-YLjF063B8bNQ-',
              defaultMessage: 'Help me choose where to go.',
              description: 'Chat message sent when a traveller asks to refine an undecided itinerary destination without a name',
            }),
        `Help narrow down the destination for stop_id=${stop.id} (${JSON.stringify(stop.placeName)}). ${stop.destinationRefinementPrompt ?? ''} Explore suitable options using the trip context. Keep this stop undecided until I choose a destination.`,
        TRIP_PLANNER_ACTION_CONTEXT_LABEL,
      ))
    } catch {
      setSendFailed(true)
    } finally {
      setSending(false)
    }
  }, [eventBus, intl, onSendMessage, placeName, stop.id, stop.placeName, stop.destinationRefinementPrompt])

  if (!prompt) return null

  const heading = stop.isPlaceLookupApplicable !== false && placeName
    ? intl.formatMessage({
        id: 'RWSSedwNZYjp1OloZ0kE',
        defaultMessage: 'Where in {placeName}?',
        description: 'Heading inviting the traveller to choose a more specific destination within a known area',
      }, { placeName })
    : intl.formatMessage({
        id: 'HhsLZgHzveX4VbzKsC_a',
        defaultMessage: 'Where are you thinking?',
        description: 'Heading inviting the traveller to explore an itinerary stop without a single known location',
      })

  return (
    <Box $compact={compact} data-testid="stop-destination-refinement">
      <Copy $compact={compact}>
        <BodyText variant="medium" weight={compact ? 'normal' : 'bold'}>{heading}</BodyText>
        {!compact && <BodyText variant="medium">{prompt}</BodyText>}
        {sendFailed && (
          <BodyText variant="small" role="alert">
            {intl.formatMessage({
              id: 'ZLlgeRV1KUAadZxW7XER',
              defaultMessage: 'Your message couldn’t be sent. Please try again.',
              description: 'Error shown when the destination exploration chat message fails to send',
            })}
          </BodyText>
        )}
      </Copy>
      <AIActionButton onClick={handleChoose} disabled={disabled || sending}>
        {intl.formatMessage({
          id: 'MD3cU3u6qfaUj4s8jE8i',
          defaultMessage: 'Help me choose',
          description: 'Button that starts a chat to help choose an undecided itinerary destination',
        })}
      </AIActionButton>
    </Box>
  )
}
